/**
 * The campaign endpoints — slice 5's client surface, plus slice 6's results.
 *
 *   listCampaigns          GET    /campaigns                  the list, paginated, with reach per row
 *   fetchCampaign          GET    /campaigns/:id              the campaign *and* what would happen if it were scheduled now
 *   createCampaign         POST   /campaigns                  a draft: template, segment, from, and optionally a time
 *   updateCampaign         PATCH  /campaigns/:id              edits a draft, and only a draft
 *   scheduleCampaign       POST   /campaigns/:id/schedule      validates, materialises, schedules
 *   cancelCampaign         POST   /campaigns/:id/cancel        stops it and settles everybody still waiting
 *   listCampaignRecipients GET    /campaigns/:id/recipients    who is in it, and why somebody is not
 *   fetchCampaignResults   GET    /campaigns/:id/results        counts by delivery state — slice 6, and slice 12's WhatsApp half
 *   fetchCampaignListSafety GET   /campaigns/:id/list-safety    slice 18: the list's health, its warm-up plan and its waves
 *   resumeHaltedCampaign   POST   /campaigns/:id/resume         restarts a campaign that stopped itself
 *   previewWhatsAppCampaign POST  /campaigns/:id/whatsapp-preview slice 49: what one real recipient would receive
 *
 * There is **no delete and no "send now"**, because the API has neither. A
 * campaign that has sent anything is a record of messages that reached real
 * people, so cancelling is how one goes away; and sending now is
 * `scheduleCampaign` with the current instant, for the reason slice 4 has
 * exactly one renderer — two send paths mean one of them is under-tested, and it
 * will be the one carrying the unusual message.
 *
 * **Nothing here decides whether a campaign may be scheduled.** The verdict is
 * `campaignScheduleIssues` in `@wend/shared`, computed by the API and returned
 * as `readiness` on the two routes below that carry it. A screen greys its
 * button from that object; it never re-derives the rule, because the way two
 * implementations of "can this send" drift is that one of them stops checking
 * the template status.
 *
 * **Nothing here can send to an address.** There is no parameter on any function
 * below that names a destination: a campaign names a *segment*, the API
 * materialises the recipients, and the send guard runs over each one there and
 * again immediately before the provider call. This module cannot reach a
 * recipient even by accident, which is the only guarantee worth having in a
 * database holding 1,287 real addresses.
 *
 * Both ends of every call are checked against the same contract: request bodies
 * are re-validated with the shared write schemas before they leave the browser,
 * and every response is parsed with the shared read shape.
 *
 * ## Slice 18's two routes, and why neither of them decides anything
 *
 * `fetchCampaignListSafety` is a **read of a verdict somebody else took**. The
 * health score, the warm-up plan and the wave states are all computed by the API
 * from `listHealth`, `warmUpPlanFor` and the wave rows, and this module parses
 * them with `campaignListSafetyResponseSchema` and hands them on. Nothing here
 * scores a list, and the schedule screen does not re-derive one: the same rule
 * `readiness` follows, for the same reason — the way two implementations of
 * "may this send" drift is that one of them stops checking.
 *
 * It is **its own read rather than a member of `fetchCampaign`**, on
 * `fetchCampaignResults`'s precedent. It answers a different question at
 * different tables — `campaign_send_waves`, the stored `list_health` snapshot,
 * and a distinct-message count over `delivery_events` — and a campaign list or a
 * poll with no interest in wave state should not pay for it.
 *
 * `resumeHaltedCampaign` is the one write in this slice, and its body is
 * `{ acknowledged: true }` because `resumeHaltedCampaignRequestSchema` types that
 * member as `z.literal(true)`: **the contract cannot express a resume that did
 * not acknowledge the halt.** So the screen must actually obtain the
 * acknowledgement rather than sending a constant — `CampaignHaltPanel` gates the
 * button on a checkbox, which is the only reading of that literal that is not a
 * lie about what the person did.
 *
 * **The halt is not resumed by re-scheduling**, and that separation is
 * deliberate: `POST /schedule` re-materialises a recipient list, and a halted
 * campaign already has one with several hundred people still `pending` in it.
 */
import {
  campaignListResponseSchema,
  campaignListSafetyResponseSchema,
  campaignRecipientListResponseSchema,
  campaignResponseSchema,
  campaignSchema,
  campaignScheduleReadinessSchema,
  campaignWaPreviewRequestSchema,
  campaignWaPreviewResponseSchema,
  createCampaignRequestSchema,
  resumeHaltedCampaignRequestSchema,
  scheduleCampaignRequestSchema,
  testSendRequestSchema,
  testSendResponseSchema,
  updateCampaignRequestSchema,
  waCampaignResultsResponseSchema,
  type Campaign,
  type CampaignListSafety,
  type CampaignListQuery,
  type CampaignListResponse,
  type CampaignRecipientListQuery,
  type CampaignRecipientListResponse,
  type CampaignWaPreview,
  type CampaignWaPreviewRequest,
  type CreateCampaignRequest,
  type ScheduleCampaignRequest,
  type TestSendRequest,
  type TestSendResponse,
  type UpdateCampaignRequest,
  type WaCampaignResultsResponse,
} from '@wend/shared';
import { z } from 'zod';

import { apiRequest } from './client';

/* -------------------------------------------------------------------------- */
/* Response envelopes                                                          */
/* -------------------------------------------------------------------------- */

/**
 * A campaign, plus what would happen if it were scheduled right now.
 *
 * A **composition of imported shapes**, never a re-declaration of one:
 * `campaignSchema` and `campaignScheduleReadinessSchema` are the contract's, and
 * nothing about either is described again here. `@wend/shared` carries both
 * halves and no envelope for the pair, and the API composes it the same way in
 * its own `campaign-view.ts` — the same situation `integrations.ts` is in with
 * `sesCheckResponseSchema`, and the same note applies: it is now written in two
 * packages, which is one more than is comfortable. It belongs in `@wend/shared`
 * the moment anything is allowed to move it.
 *
 * The pairing is not a convenience. `readiness.sendGuard.deliverable` is **how
 * many messages will actually be delivered**, and SLICES is explicit that "526
 * people" and "1 delivery" are both true in a guarded environment and that
 * showing only the first is how somebody concludes the send is broken. That
 * number has to be on the screen with the Schedule button rather than one
 * request further away, so it rides on the read.
 *
 * `.strict()`, so an API that grew a field would fail parsing here as well as
 * there rather than quietly dropping it.
 *
 * **Which is why slice 49 put nothing on it.** That slice added the WhatsApp
 * bindings, the template's positions and a nullable `fromEmail` to the campaign
 * — and every one of them rides inside `campaignSchema`, which lives in
 * `@wend/shared` and is imported by both halves. A member added to *this*
 * envelope would have to be added twice, in two packages, by two people, and
 * `.strict()` turns the half that is late into a parse failure on every
 * campaign detail page rather than a missing field. One shape, one definition,
 * one edit: nothing new goes on the envelope ever again.
 */
export const campaignWithReadinessResponseSchema = z
  .object({ campaign: campaignSchema, readiness: campaignScheduleReadinessSchema })
  .strict();
export type CampaignWithReadinessResponse = z.infer<typeof campaignWithReadinessResponseSchema>;

/** Ids come from the API, but a path segment is never interpolated raw. */
function campaignPath(campaignId: string, suffix = ''): string {
  return `/campaigns/${encodeURIComponent(campaignId)}${suffix}`;
}

/* -------------------------------------------------------------------------- */
/* Read                                                                        */
/* -------------------------------------------------------------------------- */

export async function listCampaigns(
  query: CampaignListQuery,
  signal?: AbortSignal,
): Promise<CampaignListResponse> {
  return apiRequest({
    path: '/campaigns',
    query: {
      page: String(query.page),
      pageSize: String(query.pageSize),
      sort: query.sort,
      direction: query.direction,
      // An empty search is not a filter for "nothing"; it is no filter at all.
      ...(query.search === undefined || query.search === '' ? {} : { search: query.search }),
      ...(query.status === undefined ? {} : { status: query.status }),
      ...(query.channel === undefined ? {} : { channel: query.channel }),
    },
    schema: campaignListResponseSchema,
    signal,
  });
}

/**
 * One campaign, its reach, its progress, its skip breakdown, and its readiness.
 *
 * This is also what the detail screen **polls**: the contract describes a
 * `GET /campaigns/:id/progress` and the API does not implement one, so a poll
 * re-reads the whole campaign. That is heavier than the small payload the
 * contract imagined and it is not wrong — every number on the screen is
 * refreshed by the same request, and the two shapes cannot report a different
 * vocabulary because there is only one of them. If the narrower route is ever
 * added, only `use-campaign-progress.ts` changes.
 */
export async function fetchCampaign(
  campaignId: string,
  signal?: AbortSignal,
): Promise<CampaignWithReadinessResponse> {
  return apiRequest({
    path: campaignPath(campaignId),
    schema: campaignWithReadinessResponseSchema,
    signal,
  });
}

/**
 * A page of the recipients — **the screen the skip reasons were written for.**
 *
 * `status` and `skipReason` are what make it useful. The question a person
 * actually has is never "show me the recipients"; it is "show me everybody the
 * send guard blocked", or "who is actually going to receive this". The API
 * orders sendable recipients first, so an unfiltered page opens on the people
 * the campaign is about.
 */
export async function listCampaignRecipients(
  campaignId: string,
  query: CampaignRecipientListQuery,
  signal?: AbortSignal,
): Promise<CampaignRecipientListResponse> {
  return apiRequest({
    path: campaignPath(campaignId, '/recipients'),
    query: {
      page: String(query.page),
      pageSize: String(query.pageSize),
      ...(query.status === undefined ? {} : { status: query.status }),
      ...(query.skipReason === undefined ? {} : { skipReason: query.skipReason }),
      ...(query.search === undefined || query.search === '' ? {} : { search: query.search }),
    },
    schema: campaignRecipientListResponseSchema,
    signal,
  });
}

/**
 * Counts by delivery state — slice 6's answer to "what actually happened".
 *
 * `campaign.progress.counts` (from {@link fetchCampaign}) is about each
 * *person*: pending, sent, delivered, bounced, failed, skipped. This is about
 * each *message and its credit*, from `messages`, `delivery_events` and
 * `credit_ledger` — `sent` and `delivered` stay two different numbers here for
 * the same reason they do there, and the credit totals are the only place a
 * released reservation ("a bounce did not consume a credit") can be shown.
 *
 * Not folded into `fetchCampaign`: the two answer different questions, at
 * different tables, and a screen with no interest in credits should not pay
 * for the three extra grouped reads this runs.
 *
 * **Parsed with `waCampaignResultsResponseSchema`, which is the slice-6 shape
 * plus one nullable member.** Everything both channels share — messages,
 * credits, recipients — stays in `results` and is read identically on either;
 * `whatsapp` is the channel-specific half that slice 12 added, and it is `null`
 * on an email campaign. That null is load-bearing rather than incidental, and
 * the contract argues it at length: an empty breakdown would be
 * indistinguishable from a measurement that came back with nothing in it.
 *
 * `.default(null)` on that member is what lets this land before, after or
 * without the API half of the same slice. An API that has not shipped the
 * WhatsApp results reader yet returns the old body, this parses it, and the
 * screen shows a WhatsApp campaign the sentence for "the delivery half was not
 * returned" rather than a wall of confident zeros. Widening the parse first is
 * also the ordering that cannot break: the base schema is not `.strict()`, so an
 * API that ships first would have its `whatsapp` object silently *dropped* here
 * and the screen would report a channel with no feedback while the rows sat in
 * the response — a bug with no error message anywhere.
 */
export async function fetchCampaignResults(
  campaignId: string,
  signal?: AbortSignal,
): Promise<WaCampaignResultsResponse> {
  return apiRequest({
    path: campaignPath(campaignId, '/results'),
    schema: waCampaignResultsResponseSchema,
    signal,
  });
}

/**
 * Slice 18 — how safe this list looks, how the send is broken into batches, and
 * where those batches have got to.
 *
 * One read, four states, and the shape is what keeps them apart:
 *
 *   * `health` is always present — a verdict over the addresses the campaign
 *     would reach, computed **offline**. No MX lookup, no validation vendor:
 *     1,287 real bank addresses are not sent to a third party to be scored, and
 *     that constraint is the contract's, not this module's.
 *   * `plan` is `null` for a list below `WARM_UP_MINIMUM_LIST_SIZE` — **not an
 *     empty wave list.** "No plan needed" and "a plan with no batches" are
 *     different statements and only the first is ever true.
 *   * `waves` is `null` until the campaign has been scheduled and its rows
 *     written, so a draft's preview cannot be mistaken for a send in progress.
 *   * `reading` is `null` when the rate could not be computed at all, which is
 *     not a rate of zero and must never be drawn as one.
 *
 * A failure here is **not** rendered as a clean list. The screens show the load
 * failure with its request id and a retry; nothing about a request that did not
 * come back is evidence that a list is fine.
 */
export async function fetchCampaignListSafety(
  campaignId: string,
  signal?: AbortSignal,
  options: { readonly restCritical?: boolean; readonly holdoutPercent?: number } = {},
): Promise<CampaignListSafety> {
  const response = await apiRequest({
    path: campaignPath(campaignId, '/list-safety'),
    // Slice 59: the preview as it would be with resting on, so its batches
    // match the plan the schedule commits.
    // Slice 61: likewise with a share held back.
    query: {
      restCritical: options.restCritical === true ? 'true' : undefined,
      holdoutPercent:
        options.holdoutPercent === undefined || options.holdoutPercent === 0 ? undefined : String(options.holdoutPercent),
    },
    schema: campaignListSafetyResponseSchema,
    signal,
  });
  return response.listSafety;
}

/**
 * Slice 49 — what **one real recipient** would receive, resolved by the send's
 * own code.
 *
 * ## It is a POST and it writes nothing
 *
 * The bindings being previewed are the ones on screen, **unsaved**. A preview
 * that could only run against what is already stored is a preview of the
 * previous decision: somebody changes `{{2}}` from `extras.Branch` to
 * `extras.Branch Name`, presses nothing, and reads a picture of the binding
 * they just replaced. So the list travels in the body — which a GET has no
 * honest place for — and the campaign is not touched. Saving is
 * {@link updateCampaign}, and it is a separate act.
 *
 * ## The one thing this function must never become
 *
 * **There is no substitution in the browser.** The API resolves the positions
 * with `waValuesForRecipient` and assembles the message with the same functions
 * the runner calls, and what comes back is already-resolved text. A second
 * substitution written here would be confidently right on screen and wrong on
 * the wire — the same shape as a rate computed over the wrong denominator, and
 * nobody would find out until a thousand people had received a sentence with
 * the wrong word in it. The web's job is to draw the answer, not to compute it.
 *
 * `preview.cohort` is `null` when the segment reaches nobody on WhatsApp, and
 * that null is not a zero: "nobody falls back on {{1}}" and "there was nobody
 * to count" are different facts and the screen prints only one of them.
 *
 * Allowed in any status. Seeing what a campaign that has already sent put in
 * `{{1}}` is worth as much as seeing it beforehand, and a read that refused a
 * sent campaign would leave that question answerable only from the database.
 */
export async function previewWhatsAppCampaign(
  campaignId: string,
  request: CampaignWaPreviewRequest,
  signal?: AbortSignal,
): Promise<CampaignWaPreview> {
  const response = await apiRequest({
    path: campaignPath(campaignId, '/whatsapp-preview'),
    method: 'POST',
    body: campaignWaPreviewRequestSchema.parse(request),
    schema: campaignWaPreviewResponseSchema,
    signal,
  });
  return response.preview;
}

/* -------------------------------------------------------------------------- */
/* Write                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Restarts a campaign that stopped itself because of how the messages already
 * sent were received.
 *
 * `acknowledged` is `z.literal(true)` in the contract, so this function takes no
 * argument and sends the only body the shape permits — **and the caller is
 * required to have actually obtained the acknowledgement first**, because a
 * constant sent on a person's behalf makes that literal a decoration. The panel
 * that calls it gates its button on a checkbox for exactly that reason.
 *
 * It answers with the whole campaign, like {@link cancelCampaign}, so the screen
 * never has to guess what its own write did to the status or the counts.
 *
 * **Resuming does not re-materialise anything.** Everybody the halt did not
 * reach is still `pending` on the existing recipient list; re-scheduling would
 * build a second one, which is why this is its own route rather than a second
 * use of `POST /schedule`.
 */
export async function resumeHaltedCampaign(campaignId: string): Promise<Campaign> {
  const response = await apiRequest({
    path: campaignPath(campaignId, '/resume'),
    method: 'POST',
    body: resumeHaltedCampaignRequestSchema.parse({ acknowledged: true }),
    schema: campaignResponseSchema,
  });
  return response.campaign;
}

/**
 * Creates a draft.
 *
 * A draft builds **nothing**: no recipient list exists until the campaign is
 * scheduled, so creating one cannot reach anybody. The template's status is
 * deliberately not a condition of creating — a draft campaign pointing at a
 * draft template is an ordinary Tuesday, and there is exactly one gate, which is
 * {@link scheduleCampaign}.
 *
 * `scheduledAt` is accepted here so the create screen can carry the time a
 * person had in mind onto the campaign. It schedules nothing on its own.
 */
export async function createCampaign(request: CreateCampaignRequest): Promise<Campaign> {
  const response = await apiRequest({
    path: '/campaigns',
    method: 'POST',
    body: createCampaignRequestSchema.parse(request),
    schema: campaignResponseSchema,
  });
  return response.campaign;
}

/**
 * Edits a draft — any subset of the fields.
 *
 * Only a draft: the API refuses a scheduled campaign with a 409, because its
 * recipient list already exists and changing the segment underneath one produces
 * messages nobody chose to send. Cancel, edit, schedule again.
 */
export async function updateCampaign(
  campaignId: string,
  request: UpdateCampaignRequest,
): Promise<Campaign> {
  const response = await apiRequest({
    path: campaignPath(campaignId),
    method: 'PATCH',
    body: updateCampaignRequestSchema.parse(request),
    schema: campaignResponseSchema,
  });
  return response.campaign;
}

/**
 * Schedules the campaign: validates, materialises the recipient list, and moves
 * it to `scheduled` — all in one transaction on the server, so a refusal leaves
 * no half-built worklist behind.
 *
 * **The send guard runs during this call**, per contact, and an address that is
 * not on `SEND_ALLOWLIST` becomes a `skipped` row that no send loop can pick up.
 * It runs again immediately before the provider call, because a materialised
 * list outlives the config that produced it.
 *
 * The answer carries the readiness that *allowed* the schedule, advice included
 * — which is where the quiet-hours warning and the send-guard note arrive.
 * SLICES settles that quiet hours warn and never block, and a warning that only
 * exists on a refusal is a warning nobody ever sees.
 *
 * A refusal is a 400 whose `fieldErrors` are keyed by
 * `CampaignScheduleIssueCode`, carrying **every** blocker rather than the first
 * — so a person fixes all of them instead of one per attempt.
 */
export async function scheduleCampaign(
  campaignId: string,
  request: ScheduleCampaignRequest,
): Promise<CampaignWithReadinessResponse> {
  return apiRequest({
    path: campaignPath(campaignId, '/schedule'),
    method: 'POST',
    body: scheduleCampaignRequestSchema.parse(request),
    schema: campaignWithReadinessResponseSchema,
  });
}

/**
 * Cancels the campaign.
 *
 * It cannot un-send what Amazon has already accepted; it stops the rest. Every
 * recipient still waiting becomes `skipped` with `campaign_cancelled` rather
 * than staying pending for ever, so the progress numbers settle and the
 * recipient list can still say what happened to each person.
 *
 * A **draft** may be cancelled too. That is the API route's own rule rather than
 * `isCampaignCancellable`'s: `wend_app` holds no DELETE on `campaigns`, so
 * cancelling is the only way to put an abandoned draft out of the way.
 */
export async function cancelCampaign(campaignId: string): Promise<Campaign> {
  const response = await apiRequest({
    path: campaignPath(campaignId, '/cancel'),
    method: 'POST',
    schema: campaignResponseSchema,
  });
  return response.campaign;
}

/* -------------------------------------------------------------------------- */
/* Test send                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Sends one message to one address, to see it arrive.
 *
 * No campaign, no segment, no recipient rows, and — deliberately — no
 * requirement that the template be approved: this is the thing you use *while*
 * building a template, and gating it on approval would make it useless exactly
 * when it is wanted.
 *
 * **A guard refusal is a 200, not an error.** `{ sent: false, guard }` is the
 * allow-list working, and in a development environment it is the expected
 * answer rather than a fault — so it comes back as a result the screen can
 * explain, not an exception it has to catch. The response also names the
 * transport that carried it, because a `stub` success is not a delivery and
 * this project has already lost time to a mock mistaken for the real thing.
 */
export async function testSend(request: TestSendRequest): Promise<TestSendResponse> {
  return apiRequest({
    path: '/campaigns/test-send',
    method: 'POST',
    body: testSendRequestSchema.parse(request),
    schema: testSendResponseSchema,
  });
}
